import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MediumHeader } from '@narvar/nth-header';
import { Container, Row, Column } from '@narvar/nth-grid';
import Divider from '@narvar/nth-divider';
import PickupDate from '../PickupsPage/PickupDate';
import PickupLocation from '../PickupLocation';
import Loader from '../Loader';
import InstructionsInput from '../InstructionsInput';
import {
  receivePickupInstructions,
  togglePickupInstructions,
  receivePickupPhoneNumber,
  receivePickupPhoneError,
  receivePickupTimeWindow,
} from '../../actions/pickup';
import PhoneNumberForm from '../PhoneNumberForm';
import { updateStep2Complete } from '../../actions/page';
import PickupTimeWindows from '../PickupsPage/PickupTimeWindows';

const mapStateToProps = ({
  language,
  page: { productType } = {},
  returnOptions: { selectedOptionMethodKey },
  returnsSettings: {
    [productType]: {
      returnOptions: {
        methods: { [selectedOptionMethodKey]: { pickup: { enabled, layout } = {} } = {} } = {},
      } = {},
    } = {},
  } = {},
  pickup: {
    isFetching,
    error,
    options: { timeWindows } = {},
    form: {
      date,
      address,
      showInstructions,
      instructions,
      phoneNumber,
      phoneNumberError,
      pickupTime,
    } = {},
  } = {},
}) => ({
  enabled,
  isFetching,
  language,
  layout,
  selectedOptionMethodKey,
  date,
  address,
  showInstructions,
  instructions,
  phoneNumber,
  phoneNumberError,
  error,
  timeWindows,
  pickupTime,
});

const PickupLayout = ({
  dispatch,
  enabled,
  language,
  isFetching,
  layout,
  selectedOptionMethodKey,
  date,
  address,
  showInstructions,
  instructions,
  phoneNumber,
  phoneNumberError,
  error,
  timeWindows,
  pickupTime,
}) => {
  const isTimeWindowsEnabled = Boolean(timeWindows && timeWindows.length);
  React.useEffect(() => {
    if (isFetching || error || !enabled || !isTimeWindowsEnabled) return;
    dispatch(updateStep2Complete(!!pickupTime));
  }, [dispatch, enabled, error, isFetching, pickupTime, isTimeWindowsEnabled]);

  if (isFetching) {
    return <Loader />;
  }

  if (error) {
    return (
      <Container style={{ textAlign: 'center' }}>
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <p>{language[`returns.pickup_layout.${selectedOptionMethodKey}.error`]}</p>
          </Column>
        </Row>
      </Container>
    );
  }

  if (!enabled) return null;

  const renderComponent = (component) => {
    switch (component) {
      case 'PickupDate':
        return (
          <Row style={{ padding: '0 0.5rem' }}>
            <MediumHeader s={{ fontSize: '1rem', margin: '0' }}>
              {language[`returns.pickup_layout.${selectedOptionMethodKey}.pickup_date.title`]}
            </MediumHeader>
            <PickupDate date={date} />
            <PickupTimeWindows
              timeWindows={timeWindows}
              allDayString={
                language[`returns.pickup_layout.${selectedOptionMethodKey}.pickup_date.description`]
              }
              onClick={(time) => dispatch(receivePickupTimeWindow(time))}
              selectedPickupTime={pickupTime}
            />
          </Row>
        );

      case 'PickupLocation':
        return (
          <Row style={{ padding: '0 0.5rem' }}>
            <PickupLocation
              title={
                language[`returns.pickup_layout.${selectedOptionMethodKey}.pickup_location.title`]
              }
              subtitle={
                language[
                  `returns.pickup_layout.${selectedOptionMethodKey}.pickup_location.subtitle`
                ]
              }
              address={address}
            />
          </Row>
        );

      case 'PickupInstructions':
        return (
          <Row>
            <Column md-columns={6} md-column-offset={3}>
              <InstructionsInput
                expanded={showInstructions}
                value={instructions}
                onClick={() => dispatch(togglePickupInstructions())}
                onChange={({ target: { value } }) => dispatch(receivePickupInstructions(value))}
                buttonText={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.pickup_instructions.button`
                  ]
                }
                placeholderText={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.pickup_instructions.placeholder`
                  ]
                }
                charLimit={90}
              />
            </Column>
          </Row>
        );

      case 'PhoneNumberForm':
        return (
          <Row xs-style="margin-top: 1.5rem">
            <Column md-columns={6} md-column-offset={3}>
              <PhoneNumberForm
                value={phoneNumber}
                onChange={(value) => {
                  const isIncomplete = Boolean(value && value.length !== 10 && pickupTime);
                  dispatch(receivePickupPhoneNumber(value));
                  dispatch(updateStep2Complete(!isIncomplete));
                }}
                onBlur={() => {
                  const isIncomplete = Boolean(
                    phoneNumber && phoneNumber.length !== 10 && pickupTime,
                  );
                  dispatch(receivePickupPhoneError(isIncomplete));
                  dispatch(updateStep2Complete(!isIncomplete));
                }}
                header={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.header`
                  ]
                }
                headerTag={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.header_tag`
                  ]
                }
                description={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.description`
                  ]
                }
                placeholder={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.placeholder`
                  ]
                }
                footer={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.footer`
                  ]
                }
                error={phoneNumberError}
                errorMessage={
                  language[
                    `returns.pickup_layout.${selectedOptionMethodKey}.phone_number_form.error`
                  ]
                }
              />
            </Column>
          </Row>
        );

      case 'Divider':
        return <Divider />;

      default:
        return null;
    }
  };

  return (
    <Container style={{ textAlign: 'center', marginTop: '1rem' }}>
      {layout.map((component, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`${component}-${idx}`}>{renderComponent(component)}</div>
      ))}
    </Container>
  );
};

PickupLayout.defaultProps = {
  enabled: false,
  date: '',
  layout: [],
  address: {},
  error: false,
  showInstructions: false,
  instructions: '',
  phoneNumber: '',
  phoneNumberError: false,
};

PickupLayout.propTypes = {
  dispatch: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
  isFetching: PropTypes.bool.isRequired,
  language: PropTypes.object.isRequired,
  layout: PropTypes.array,
  selectedOptionMethodKey: PropTypes.string.isRequired,
  date: PropTypes.string,
  address: PropTypes.shape({
    street_1: PropTypes.string,
    street_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  showInstructions: PropTypes.bool,
  instructions: PropTypes.string,
  phoneNumber: PropTypes.string,
  phoneNumberError: PropTypes.bool,
  error: PropTypes.bool,
  timeWindows: PropTypes.arrayOf(PropTypes.string).isRequired,
  pickupTime: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(PickupLayout);
